import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { socket, leavePoll as apiLeavePoll, getPollBySlug } from '../services/pocketbaseApi';
import { useNavigate } from 'react-router-dom';

const PollContext = createContext();

export function PollProvider({ children }) {
  const [poll, setPoll] = useState(null);
  const [loading, setLoading] = useState(false);
  const [participantName, setParticipantName] = useState(() => {
    const match = window.location.pathname.match(/poll\/([^/]+)/);
    if (match) {
      const slug = match[1];
      const key = `poll_${slug}`;
      console.log(`Initializing participantName for slug: ${slug}`);
      console.log(`Looking for localStorage key: ${key}`);

      try {
        const stored = localStorage.getItem(key);
        console.log(`Raw localStorage value:`, stored);

        if (stored) {
          const parsed = JSON.parse(stored);
          console.log(`Parsed localStorage value:`, parsed);
          return parsed?.name || '';
        }
      } catch (e) {
        console.log(`Error parsing localStorage:`, e);
      }
    }
    console.log(`No participant name found, returning empty string`);
    return '';
  });
  const unsubscribeRef = useRef(null);
  const loadingRef = useRef(false); // Add ref to track loading state
  const navigate = useNavigate();

  // Auto-load poll data when URL contains poll slug
  useEffect(() => {
    const loadPollFromUrl = async () => {
      const match = window.location.pathname.match(/poll\/([^/]+)/);
      if (match && !poll && !loadingRef.current) { // Check ref to prevent duplicate loads
        const slug = match[1];
        loadingRef.current = true; // Set loading flag
        setLoading(true);
        try {
          const pollData = await getPollBySlug(slug);
          setPoll(pollData);

          // Check if the user was already a participant from localStorage
          const storedParticipant = localStorage.getItem(`poll_${slug}`);
          let shouldRedirect = false;

          if (storedParticipant) {
            try {
              const { name } = JSON.parse(storedParticipant);
              // Verify this participant still exists in the poll
              const participantExists = pollData.participants?.some(p => p.name === name);
              if (participantExists) {
                setParticipantName(name);
                console.log(`Restored participant: ${name}`);
              } else {
                // Participant no longer exists, clear localStorage and redirect to join
                console.log(`Participant ${name} no longer exists in poll, redirecting to join`);
                localStorage.removeItem(`poll_${slug}`);
                shouldRedirect = true;
              }
            } catch (e) {
              // Invalid localStorage data, clear it and redirect
              console.log('Invalid localStorage data, redirecting to join');
              localStorage.removeItem(`poll_${slug}`);
              shouldRedirect = true;
            }
          } else {
            // No stored participant, redirect to join page
            console.log('No stored participant, redirecting to join');
            shouldRedirect = true;
          }

          if (shouldRedirect) {
            navigate(`/?poll=${slug}`);
            return;
          }

          // Set up real-time updates only if we're staying on this page
          socket.emit('join_poll', pollData.id);
          const event = `poll_update_${pollData.id}`;
          socket.on(event, setPoll);

          unsubscribeRef.current = () => {
            socket.emit('leave_poll', pollData.id);
            socket.off(event, setPoll);
          };
        } catch (error) {
          console.error('Failed to load poll:', error);
          // Redirect to home if poll not found
          navigate('/');
        } finally {
          setLoading(false);
          loadingRef.current = false; // Reset loading flag
        }
      }
    };

    loadPollFromUrl();
  }, [poll, navigate]); // Remove participantName dependency to prevent re-runs

  const joinPoll = useCallback(async (pollData, name) => {
    setPoll(pollData);
    setParticipantName(name);

    socket.emit('join_poll', pollData.id);
    const event = `poll_update_${pollData.id}`;
    socket.on(event, setPoll);

    unsubscribeRef.current = () => {
      socket.emit('leave_poll', pollData.id);
      socket.off(event, setPoll);
    };

    localStorage.setItem(`poll_${pollData.slug}`, JSON.stringify({ name, pollName: pollData.name }));
  }, []);

  const leavePoll = useCallback(async () => {
    if (poll && participantName) {
      try {
        console.log(`Leaving poll: ${poll.name} as participant: ${participantName}`);
        const result = await apiLeavePoll(poll.id, participantName);
        console.log('Successfully left poll:', result);
      } catch (e) {
        console.error('Error leaving poll:', e);
        // Don't prevent leaving if API call fails - user should still be able to leave
      }
    }

    // Clean up socket connections
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // Clean up localStorage and navigate away
    if (poll) {
      localStorage.removeItem(`poll_${poll.slug}`);
      navigate(`/`);
    }

    // Reset local state
    setPoll(null);
    setParticipantName('');
  }, [poll, participantName, navigate]);

  useEffect(() => {
    return () => {
      if (unsubscribeRef.current) unsubscribeRef.current();
    };
  }, []);

  const value = {
    poll,
    loading,
    participantName,
    setParticipantName,
    joinPoll,
    leavePoll,
  };

  return (
    <PollContext.Provider value={value}>
      {children}
    </PollContext.Provider>
  );
}

export function usePoll() {
  return useContext(PollContext);
}
